/* Counterparty detail — /sales/counterparties/:id. Spec/03 archetype B (the shared `dp-*` chrome
   in `component/detail-page.scss`): a non-sticky head + a `minmax(0,720px) 320px` grid of a tabbed
   main column and a reference rail.

   One object load: `GET excalibur/counterparties/:id/` via `getAbortable` + an `AbortController`,
   as `counterparties/List.js` reads the list. `getAbortable` resolves the parsed body (or null on
   401/403, where it has already redirected to /login): a 404 or a body with no `id` is "not found",
   any other failure is an error state with a Retry. `Page` keys the card by the route's id, so
   another counterparty is a fresh mount, not a reused one.

   Edit is a whole-card mode toggle (the `merchandiser/Page.js` shape): a "Edit" button in the head
   swaps VIEW for EDIT, Save/Cancel replace it. Writable: the six `EDIT_FIELDS` —
   assigned_supervisor, client_type, client_category, sales_channel, start_cooperation_at,
   lifecycle_status — the lifecycle's manual flag (`lifecycle_is_manual`), the company's additional fields (`custom_fields`), and a
   photo REPLACEMENT (it cannot be cleared). name,
   address, phone_number and department belong to 1С and are read-only here: the API silently
   ignores any other key (no 400), so nothing else is ever sent — an extra key would be a no-op
   that only reads as a change.

   Saving is up to two requests. First a JSON `PATCH` of ONLY the fields that changed — a cleared
   field goes as `null`, never `""` (a blank date is a 400). Only-changed is load-bearing, not
   thrift: the API marks the lifecycle as set by hand when the status it receives differs from the
   stored one, so sending back a stale, untouched status would flip `lifecycle_is_manual`. Then, if
   a file was staged and the first request landed, a multipart `PATCH` of `photo` alone through
   `callForm` (`call` always `JSON.stringify`s, and a File cannot survive that). A photo-only save
   skips the JSON request. Success is `isOk` AND a record with an `id` — a failed write hands back a
   TRUTHY `failedResponse()`, and a 404 a parsed `{detail}` that `isOk` passes. Each response is the full record in read form, so the card and its
   pristine baseline refresh from it with no second GET; if the photo request fails after the fields
   landed, the fields stay saved, a warning toast says exactly that, the file stays staged and the
   next Save sends the photo alone.

   A 400 is the raw `Response` (`action.js`); `parseErrors` reads its per-key body. A key that is a
   form field marks that field with one translated phrase — the API's own English message is never
   shown — `photo` reports beside the Upload button, and anything else falls back to the toast.

   Back preserves the list's filters: the list rides its whole filter set in the query string, so
   the row that opened this page hands that `search` over in route state and the breadcrumb pushes
   it back. Opened directly by URL there is no state, so Back goes to the bare list. Leaving the card
   with unsaved edits or an unsent comment asks first (`leaveTo`). */

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Avatar, Button, Card, DatePicker, Descriptions, Empty, Flex, Form, Modal, Select, Skeleton, Space, Switch, Tooltip, Typography, Upload } from "antd";
import {
  ApartmentOutlined,
  CloseOutlined,
  EditOutlined,
  ExceptionOutlined,
  FileTextOutlined,
  LinkOutlined,
  NumberOutlined,
  CommentOutlined,
  ContactsOutlined,
  ProfileOutlined,
  PushpinOutlined,
  RightOutlined,
  RollbackOutlined,
  SaveOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UploadOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";

import Alert from "/src/util/alert";

import { KpiStrip } from "../../component/detail-page";
import EntitySelect from "../../component/EntitySelect";
import { dash, fullName, initialsOf, PersonCell, textOf } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, ORDERS_PAGE, SIDE_NAV, SUPERVISOR_DETAIL, TRANSLATION_DOWNLOADER, VISIT_PAGE } from "../../config/lang";
import { call, callForm, getAbortable, isOk, parseErrors } from "../../util/action";
import dayjs from "../../util/dayjs";
import { relativeTime } from "../../util/relative-time";
import { translate } from "../../util/util";

import AddressesTab, { NoPointsNotice } from "./AddressesTab";
import { cfDisplay, cfEditor, cfFieldId, cfHint, cfWire } from "./customFields";
import CommentsTab from "./CommentsTab";
import ContactsTab from "./ContactsTab";
import DebtsTab from "./DebtsTab";
import LinksSection from "./LinksSection";
import OrdersTab from "./OrdersTab";
import PaymentsTab from "./PaymentsTab";
import ReturnsTab from "./ReturnsTab";
import { linkClick, money, ReadOnlyTag, TabHead } from "./tabParts";
import { LIFECYCLE_STATUSES, lifecycleTags as sharedLifecycleTags, statusTag as sharedStatusTag } from "./status";
import "./counterparties.scss";

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

/* The seven keys `PATCH excalibur/counterparties/:id/` accepts besides `photo`. The first four are
   references the card holds as objects (`{id, first_name, last_name, photo}` for the supervisor,
   `{id, label}` for a dictionary value) and the API takes as a bare id. */
const REF_FIELDS = ["assigned_supervisor", "client_type", "client_category", "sales_channel"];
const EDIT_FIELDS = [...REF_FIELDS, "start_cooperation_at", "lifecycle_status"];

/* The editable slice in the shape the API takes it, so the dirty check ignores every read-only
   field and "no value" is always `null`. A File is invisible to JSON.stringify, so a staged photo
   is tracked by its own flag rather than here. */
const editable = o => Object.fromEntries(EDIT_FIELDS.map(key => [key, (REF_FIELDS.includes(key) ? o[key]?.id : o[key]) ?? null]));

/* The additional fields that differ from the pristine record, as `custom_fields` items in the
   card's order — that order is what a 400's per-item errors are matched back to. */
const changedCustomFields = (item, original) => {
  const was = Object.fromEntries((original.custom_fields || []).map(f => [f.id, cfWire(f)]));
  return (item.custom_fields || [])
    .filter(f => JSON.stringify(cfWire(f)) !== JSON.stringify(was[f.id] ?? null))
    .map(f => {
      const value = cfWire(f);
      return { field_id: f.id, value: f.type === "text" && value !== null ? value.trim() : value };
    });
};

/* What the API decides about `lifecycle_is_manual` when the key is NOT sent — its rule from before
   the key was writable: a stage changed to another one → manual, a stage cleared → automatic, an
   untouched stage → the saved flag stays. */
const impliedManual = (item, original) => {
  const stage = item.lifecycle_status ?? null;
  if (stage === (original.lifecycle_status ?? null)) return Boolean(original.lifecycle_is_manual);
  return stage !== null;
};

/* Only what differs from the pristine record — the PATCH body. Additional fields ride in the same
   JSON request as `custom_fields`; with nothing else changed, that key is the whole body.
   `lifecycle_is_manual` is sent ONLY when the switch says something the API would not decide on its
   own (`impliedManual`): switching manual off, switching it on with the stage untouched, or a new
   stage with the switch turned off. A new stage with the switch on (as picking it turns it on) goes
   without the key: the API (where `lifecycle_is_manual` is writable) marks the stage as manual on
   its own, so the body stays the minimal diff. */
const changedFields = (item, original) => {
  const now = editable(item);
  const was = editable(original);
  const body = Object.fromEntries(EDIT_FIELDS.filter(key => now[key] !== was[key]).map(key => [key, now[key]]));
  const manual = Boolean(item.lifecycle_is_manual);
  if (manual !== impliedManual(item, original)) body.lifecycle_is_manual = manual;
  const custom = changedCustomFields(item, original);
  if (custom.length) body.custom_fields = custom;
  return body;
};
/* The form's keys in its own order — where a refused save puts the focus first. */
const FORM_ORDER = [...REF_FIELDS, "start_cooperation_at", "lifecycle_status", "lifecycle_is_manual"];

/* The form's error key of an additional field. */
const cfKey = id => `cf:${id}`;

/* Fixed picker scopes, as module constants: `EntitySelect` keys its fetch callbacks on the
   `extraQuery` identity, so an inline literal would rebuild them on every render. The three
   dictionaries share one endpoint and differ by `kind`; it lists live values only (a deleted one
   is soft-deleted and gone from the list). `is_active` keeps deactivated supervisors out. The API
   refuses to newly assign either; one already on the card still shows by name, from `seed` (the
   card sends `{id, label}` even for a deleted value), and is never re-sent unless changed. */
const SUPERVISOR_QUERY = { is_active: true };
const DICTIONARY_QUERY = {
  client_type: { kind: "client_type" },
  client_category: { kind: "client_category" },
  sales_channel: { kind: "sales_channel" },
};

const fieldId = key => `cp-field-${key}`;

/* Sections, navigated by the visit page's rail-of-pills pattern (`vd-rail` + `vd-sec`) rather than
   antd's underline `<Tabs>`, so the counterparty card and the visit review read as one product.
   One section is mounted at a time (only `sectionBody` renders), same as the visit's rail. */
const SECTIONS = [
  { key: "overview", labelKey: "tab_overview", icon: <ProfileOutlined /> },
  /* The address book — spec/02's icon map; the nav's person glyphs (`Idcard`, `Team`) stay distinct. */
  { key: "contacts", labelKey: "tab_contacts", icon: <ContactsOutlined /> },
  /* The points glyph (spec/02's `pin_drop`): the tab opens on the counterparty's retail points. */
  { key: "addresses", labelKey: "tab_addresses", icon: <PushpinOutlined /> },
  { key: "orders", labelKey: "tab_orders", icon: <ShoppingCartOutlined /> },
  { key: "returns", labelKey: "tab_returns", icon: <RollbackOutlined /> },
  /* The glyph the visit page gives its payments section (`visit/View.js`). */
  { key: "payments", labelKey: "tab_payments", icon: <FileTextOutlined /> },
  /* A document with a "!" — the Payments tab's plain document, unpaid (spec/02's icon map). */
  { key: "debts", labelKey: "tab_debts", icon: <ExceptionOutlined /> },
  /* The speech bubble with lines — spec/02; `MessageOutlined` is a contact's Viber. */
  { key: "comments", labelKey: "tab_comments", icon: <CommentOutlined /> },
];
/* The card's sections, as `?tab=` names them. */
const TAB_KEYS = SECTIONS.map(section => section.key);
/* The document tabs' heading: where "All …" goes and what it says (`tabParts.js`'s `TabHead`).
   Payments and debts have no list of their own, so their heading is "Read-only" and the window
   alone — for debts, "open debts" rather than a date window. */
const DOC_TABS = {
  orders: { listPath: "/sales/orders", allKey: "orders_all" },
  returns: { listPath: "/sales/returns", allKey: "returns_all" },
  payments: {},
  debts: { windowKey: "debts_window" },
};

/* The sync state is the card's alone (the list does not show it); the lifecycle tones, the label
   map and the pill itself are shared with the list — `./status`. */
const SYNC_TONE = { synced: "ok", pending: "wait", failed: "no" };

/* `metrics/`'s per-status split of `orders_90d`, in the order the tooltip lists it. Cancelled
   orders are not in the number at all, which the tooltip says. */
const ORDER_STATUSES = ["new", "processing", "sent_for_delivering", "closed"];

/* The card proper, mounted once per counterparty: `Page` below keys it by the route's id, so moving
   to another counterparty (a linked one, Back / Forward) starts it afresh — every state, every tab
   and its modals — and a request still in flight for the previous card lands on an unmounted
   component instead of on this one. */
const CardPage = ({ id }) => {
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector(state => state.me?.language || "en");
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);

  const [item, setItem] = useState(null);
  const [original, setOriginal] = useState(null);
  /* The Comments tab's unsent note — here, above the tabs, so switching tabs keeps it (a tab is
     mounted only while open). Cleared when the card changes. */
  const [commentDraft, setCommentDraft] = useState("");
  const [status, setStatus] = useState("loading"); // loading | notfound | error | ready
  /* Bumped by the error state's Retry. */
  const [reloadKey, setReloadKey] = useState(0);
  /* A refused field to focus once the form is on screen (a Save from another tab switches to the
     Overview first). */
  const [focusTarget, setFocusTarget] = useState(null);
  const [mode, setMode] = useState("VIEW"); // VIEW | EDIT
  const [busy, setBusy] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  /* What the last save was refused for: `errors` by field key (a flag — the API's message is
     English and is not shown), `photoError` for the photo request. */
  const [errors, setErrors] = useState({});
  const [photoError, setPhotoError] = useState(false);
  /* `GET …/metrics/` — the 90-day order count for the head strip. Its own request beside the card's,
     so a slow or failed one never holds the card back: the tile shows a skeleton, then the number
     or a dash (no toast — the card itself is fine). Fetched per counterparty, not after a save:
     nothing the card edits changes it. */
  const [metrics, setMetrics] = useState({ status: "loading", data: null }); // loading | ok | error

  const editing = mode === "EDIT";

  /* The open section lives in the URL (`?tab=`), so a reload, a shared link and the order page's
     Back all land on it. Switching REPLACES the entry — a tab is not a place in history — and
     carries the route state along, or the breadcrumb would lose the list's filters. Anything
     unknown reads as the Overview. */
  const tabParam = new URLSearchParams(location.search).get("tab");
  const tab = TAB_KEYS.includes(tabParam) ? tabParam : "overview";
  const selectTab = key => history.replace({ pathname: location.pathname, search: `?tab=${key}` }, location.state);
  /* A payment's debt opens the Debts tab pointed at that debt (`?tab=debts&debt=<id>`) — a tab
     switch like `selectTab`, so the same route state rides along. The id is in the URL rather than
     in state so it needs no merging with the card's state and survives a reload; picking any tab
     from the rail writes a bare `?tab=` and drops it. */
  const debtTo = debtId => ({ pathname: location.pathname, search: `?tab=debts&debt=${encodeURIComponent(debtId)}`, state: location.state });
  const debtParam = new URLSearchParams(location.search).get("debt");
  /* AD-4's "Create point" (`?tab=addresses&create=point`): the Addresses tab opens the right modal
     once its address list is in, then drops the parameter — a reload does not reopen it. A URL
     parameter, as `&debt=`, because the notice sits in the head while the addresses load in the tab. */
  const createIntent = new URLSearchParams(location.search).get("create") === "point";
  const openCreatePoint = () => history.replace({ pathname: location.pathname, search: "?tab=addresses&create=point" }, location.state);
  const createIntentDone = useCallback(() => history.replace({ pathname: location.pathname, search: "?tab=addresses" }, location.state), [
    history,
    location.pathname,
    location.state,
  ]);

  /* `getAbortable`, as the list uses it: the controller aborts on unmount. A 404 (or a body with
     no id) is "not found"; anything else failed to load and offers a retry (spec/03 "Error"). */
  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    getAbortable(`excalibur/counterparties/${id}/`, controller.signal)
      .then(response => {
        if (!response) return; // 401/403 — already redirected to /login
        if (!response.id) {
          setStatus("notfound");
          return;
        }
        setItem(response);
        setOriginal(structuredClone(response));
        setStatus("ready");
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setStatus(err.message === "HTTP 404" ? "notfound" : "error");
      });
    return () => controller.abort();
  }, [id, reloadKey]);

  useEffect(() => {
    const controller = new AbortController();
    getAbortable(`excalibur/counterparties/${id}/metrics/`, controller.signal)
      .then(response => {
        if (!response) return; // 401/403 — already redirected to /login
        setMetrics(Number.isFinite(response.orders_90d) ? { status: "ok", data: response } : { status: "error", data: null });
      })
      .catch(err => {
        if (err.name === "AbortError") return;
        setMetrics({ status: "error", data: null });
      });
    return () => controller.abort();
  }, [id]);

  /* The staged photo's preview is an object URL: released when it is replaced, dropped or the card
     unmounts. */
  useEffect(() => () => photoPreview && URL.revokeObjectURL(photoPreview), [photoPreview]);

  useEffect(() => {
    if (!focusTarget) return;
    const el = document.getElementById(focusTarget);
    if (!el) return;
    el.focus();
    setFocusTarget(null);
  }, [focusTarget, tab]);

  /* The list's `?…` ride-along, so Back returns to the exact filtered view it was opened from. */
  const backSearch = location.state?.from || "";
  const backTo = `/sales/counterparties${backSearch}`;

  /* Copy the card's own URL. `navigator.clipboard` is absent on insecure origins and can reject
     without a user gesture / permission, so the write is guarded and every failure surfaces one
     error toast rather than throwing. */
  const copyLink = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(window.location.href);
      Alert.success(t("card_copied"), { position: "top-right" });
    } catch {
      Alert.error(t("card_copy_error"), { position: "top-right" });
    }
  };

  /* ---------------------------------------------------------------- editing */

  const patch = item && original ? changedFields(item, original) : {};
  const fieldsDirty = Object.keys(patch).length > 0;
  const dirty = fieldsDirty || Boolean(photoFile);

  /* Changing a field withdraws the refusal it was marked with — the value it was refused for is gone.
     The same for an additional field. */
  const setCustomField = (fieldId, value) => {
    setItem(prev => ({ ...prev, custom_fields: (prev.custom_fields || []).map(f => (f.id === fieldId ? { ...f, value } : f)) }));
    setErrors(prev => (prev[cfKey(fieldId)] ? { ...prev, [cfKey(fieldId)]: false } : prev));
  };
  const setField = (field, value) => {
    setItem(prev => ({ ...prev, [field]: value }));
    setErrors(prev => (prev[field] ? { ...prev, [field]: false } : prev));
  };
  /* Picking a stage is setting it by hand, so the switch turns on where the user can see it (and
     can turn it off again before saving); clearing the stage turns it off — manual needs a stage. */
  const setStage = value => {
    setItem(prev => ({ ...prev, lifecycle_status: value, lifecycle_is_manual: value !== null }));
    setErrors(prev => (prev.lifecycle_status || prev.lifecycle_is_manual ? { ...prev, lifecycle_status: false, lifecycle_is_manual: false } : prev));
  };
  /* EntitySelect hands back the full option object (or null). Store only what the card reads and
     the `seed` re-labels from — the API's own read shape — and `null` to clear the field. */
  const pickSupervisor = option =>
    setField(
      "assigned_supervisor",
      option ? { id: idOf(option), first_name: option.first_name, last_name: option.last_name, photo: option.photo ?? null, label: option.label } : null
    );
  const pickValue = field => option => setField(field, option ? { id: idOf(option), label: option.label || option.name } : null);

  const resetEdit = () => {
    setItem(structuredClone(original));
    setPhotoFile(null);
    setPhotoPreview(null);
    setErrors({});
    setPhotoError(false);
    setMode("VIEW");
  };

  /* Linked counterparties and points are written through their own endpoints, not the form's PATCH,
     and work in either mode. Their answer is merged — that part alone — into the shown record AND the
     pristine baseline (`mergeBoth`): `dirty` compares the form's fields and photo only, so an open
     form keeps every unsaved edit, its staged photo and its marks. Unlinking a point answers with the
     full card; only its points and their count are taken. */
  const mergeBoth = fn => {
    setItem(prev => (prev ? fn(prev) : prev));
    setOriginal(prev => (prev ? fn(prev) : prev));
  };
  const mergePoints = card => {
    const points = { points: Array.isArray(card.points) ? card.points : [], points_count: card.points_count ?? 0 };
    mergeBoth(prev => ({ ...prev, ...points }));
  };
  /* AD-3: a point made from (or tied to) an address joins the block — once: tying a point the
     counterparty already had changes nothing — in the API's order (active first, then by name). */
  const mergePoint = point =>
    mergeBoth(prev => {
      if (!point || (prev.points || []).some(p => p.id === point.id)) return prev;
      const points = [...(prev.points || []), point].sort(
        (a, b) => Number(b.is_active !== false) - Number(a.is_active !== false) || String(a.name).localeCompare(String(b.name)) || a.id - b.id
      );
      return { ...prev, points, points_count: (prev.points_count ?? 0) + 1 };
    });
  const mergeLinks = list => mergeBoth(prev => ({ ...prev, linked_counterparties: list }));
  /* A linked counterparty's card, carrying THIS card's list filters on, so its breadcrumb returns
     to the same filtered list (the browser's Back returns here). */
  const linkedPath = otherId => `/sales/counterparties/${otherId}`;
  const linkTo = otherId => ({ pathname: linkedPath(otherId), state: { from: backSearch } });
  /* Every way out of the card goes through here: a linked counterparty, and every link in the
     Orders tab and its modal. Leaving with unsaved edits asks first — the same dialog Cancel shows;
     the next page would otherwise drop the form without a word. It navigates itself (`event` may
     be null, from a modal link), so a `<Link>`'s own navigation is cancelled. A modified click
     (new tab) never reaches this: the callers let the browser have it. */
  /* "Discard unsaved changes?" — leaving the card and Cancel ask the same. Static `Modal.confirm`
     renders outside the tree, so it does not inherit AppShell's `ConfigProvider locale` — pass both
     button labels or Cancel stays English. */
  const confirmDiscard = onOk =>
    Modal.confirm({
      title: t("edit_discard_confirm"),
      okText: t("edit_discard_ok"),
      okButtonProps: { danger: true },
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk,
    });
  const leaveTo = (event, to) => {
    event?.preventDefault?.();
    /* The Comments tab's unsent note counts too — it lives here, and leaving drops it. */
    if (!(editing && dirty) && !commentDraft.trim()) return history.push(to);
    confirmDiscard(() => history.push(to));
  };
  const openLinked = (event, otherId) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    leaveTo(event, linkTo(otherId));
  };

  const cancelEdit = () => {
    if (!dirty) return resetEdit();
    confirmDiscard(resetEdit);
  };

  /* VIEW's "Return to automatic mode": one PATCH of the flag alone. The stage stays as it is — the
     next automatic run may change it, which the question says. The card refreshes from the answer
     (the full record), so the overview's note and the head's icon go with it. An API that does not
     know the key yet answers 400: said in a toast, the card untouched. */
  const revertManual = () =>
    Modal.confirm({
      title: t("lc_manual_revert_title"),
      content: t("lc_manual_revert_body").replace("{stage}", t(`lc_${original.lifecycle_status}`)),
      okText: t("lc_manual_revert_ok"),
      cancelText: translate(TRANSLATION_DOWNLOADER, "cancel", lang),
      onOk: () =>
        new Promise(resolve =>
          call({ method: "PATCH", url: `excalibur/counterparties/${id}/`, post: { lifecycle_is_manual: false } }, response => {
            resolve();
            if (isOk(response) && response.id) {
              setItem(response);
              setOriginal(structuredClone(response));
              Alert.success(t("lc_manual_reverted_msg"), { position: "top-right" });
              return;
            }
            if (typeof response?.json === "function") Alert.warning(t("lc_manual_refused"), { position: "top-right" });
            else if (response?.detail) Alert.warning(t("edit_error_msg"), { position: "top-right" });
          })
        ),
    });

  const save = () => {
    const url = `excalibur/counterparties/${id}/`;
    const warn = () => Alert.warning(t("edit_error_msg"), { position: "top-right" });

    /* The response is the full card: it becomes both the shown record and the pristine baseline. */
    const adopt = response => {
      setItem(response);
      setOriginal(structuredClone(response));
    };
    const finish = () => {
      setBusy(false);
      setPhotoFile(null);
      setPhotoPreview(null);
      setMode("VIEW");
      Alert.success(t("edit_saved_msg"), { position: "top-right" });
    };
    /* `onKeys` gets the refused keys of a 400 and returns true once it has shown them. A failure
       that is not a 400 is a 5xx / network error, which `action.js` has already toasted. */
    const refused = (response, onKeys) => {
      setBusy(false);
      if (typeof response?.json !== "function") {
        if (!response?.failed) warn();
        return;
      }
      parseErrors(response).then(parsed => {
        if (!onKeys(Object.keys(parsed.fields))) warn();
      });
    };

    /* `afterFields`: the JSON request of this same Save has already landed and been adopted. */
    const sendPhoto = afterFields => {
      const fd = new FormData();
      fd.append("photo", photoFile);
      callForm({ method: "PATCH", url, post: fd }, response => {
        /* A 404 hands back a parsed `{detail}`, which `isOk` reads as success — the record is the proof. */
        if (isOk(response) && response.id) {
          adopt(response);
          finish();
          return;
        }
        /* Either way the file stays staged, so the next Save is this request alone. */
        const markPhoto = keys => {
          if (!keys.includes("photo")) return false;
          setPhotoError(true);
          return true;
        };
        if (!afterFields) {
          refused(response, markPhoto); // nothing was saved — the ordinary failure path
          return;
        }
        /* Half of the Save landed: say so in one toast, in place of the "couldn't save" one —
           that would be false for the fields. */
        setBusy(false);
        Alert.warning(t("edit_partial_msg"), { position: "top-right" });
        if (typeof response?.json === "function") parseErrors(response).then(parsed => markPhoto(Object.keys(parsed.fields)));
      });
    };

    setBusy(true);
    setErrors({});
    setPhotoError(false);
    if (!fieldsDirty) {
      sendPhoto(false); // `dirty` with no changed field means a staged file
      return;
    }
    call({ method: "PATCH", url, post: patch }, response => {
      if (isOk(response) && response.id) {
        adopt(response);
        if (photoFile) sendPhoto(true);
        else finish();
        return;
      }
      if (typeof response?.json !== "function") {
        refused(response, () => false); // a 5xx / network failure — `action.js` toasted it
        return;
      }
      /* A 400, read RAW: `parseErrors` flattens arrays to text, and `custom_fields` is a list of
         per-item errors in the order sent — item i is `patch.custom_fields[i]`'s field. */
      response
        .json()
        .catch(() => ({}))
        .then(body => {
          setBusy(false);
          const keys = Object.keys(body || {});
          const known = keys.filter(key => FORM_ORDER.includes(key));
          const custom = {};
          if (Array.isArray(body?.custom_fields)) {
            body.custom_fields.forEach((error, index) => {
              const sent = patch.custom_fields?.[index];
              if (!sent || !error || !Object.keys(error).length) return;
              const field = (item.custom_fields || []).find(f => f.id === sent.field_id);
              custom[cfKey(sent.field_id)] = field?.type === "choice" ? "field_error" : "ad_field_invalid";
            });
          }
          setErrors({ ...Object.fromEntries(known.map(key => [key, true])), ...custom });
          /* spec/03 "Failed save": focus the first refused field in the form's order. */
          const first = known.length
            ? fieldId(FORM_ORDER.find(key => known.includes(key)))
            : (() => {
                const f = (item.custom_fields || []).find(x => custom[cfKey(x.id)]);
                return f ? cfFieldId(f) : null;
              })();
          /* The form is mounted on the Overview only; a Save from another tab goes there first. */
          if (first) {
            if (tab !== "overview") selectTab("overview");
            setFocusTarget(first);
          }
          /* A key with nowhere to show (or a list with no field error) — the toast covers it. */
          const shown = known.length + (Object.keys(custom).length ? 1 : 0);
          const expected = keys.length;
          if (!shown || shown < expected) warn();
        });
    });
  };

  /* ----------------------------------------------------------------- states */

  if (status === "loading") {
    return (
      <div className="shell-content">
        <Skeleton active title paragraph={{ rows: 2 }} />
        <div className="dp-grid">
          <Skeleton active paragraph={{ rows: 8 }} />
          <Skeleton active paragraph={{ rows: 5 }} />
        </div>
      </div>
    );
  }

  if (status !== "ready" || !item) {
    const notFound = status === "notfound";
    return (
      <div className="shell-content">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <Flex vertical gap={8} align="center">
              <Typography.Text strong>{notFound ? t("card_not_found_title") : translate(SUPERVISOR_DETAIL, "load_error", lang)}</Typography.Text>
              {notFound ? <Typography.Text type="secondary">{t("card_not_found_desc")}</Typography.Text> : null}
              <Space size={8} wrap>
                {notFound ? null : <Button onClick={() => setReloadKey(k => k + 1)}>{t("retry")}</Button>}
                <Button type="primary" onClick={event => leaveTo(event, backTo)}>
                  {t("card_back")}
                </Button>
              </Space>
            </Flex>
          }
        />
      </div>
    );
  }

  const name = item.name || `#${item.id}`;
  /* The identity strip's meta line — department, supervisor, responsible, external code — each an icon+value
     span rendered ONLY when present (an absent field leaves no gap and no dash placeholder), the
     same icon-led sub-line the visit card (`vd-id__sub`) uses. */
  /* The head describes the SAVED record: while editing, `item` holds unsaved picks, and a status
     pill that changed before Save — beside a "manual" mark that could not — would state something
     the server has not been told. */
  const saved = original || item;

  /* The 90-day order count: a link to the Orders tab (a tab switch, so unsaved edits stay), its
     per-status split in a tooltip on hover AND keyboard focus. */
  const ordersKpi =
    metrics.status === "loading" ? (
      <Skeleton.Input active size="small" className="cp-kpi-skel" />
    ) : metrics.status === "ok" ? (
      <Tooltip
        trigger={["hover", "focus"]}
        title={
          <div className="cp-kpi-tip">
            {ORDER_STATUSES.map(s => (
              <div key={s}>
                {translate(ORDERS_PAGE, `order_status_${s}`, lang)}: <span className="lp-num">{metrics.data.orders_90d_by_status?.[s] ?? 0}</span>
              </div>
            ))}
            <div className="cp-kpi-tip__note">{t("m_orders_no_cancelled")}</div>
          </div>
        }>
        <Button type="link" className="cp-kpi-link" onClick={() => selectTab("orders")}>
          {metrics.data.orders_90d}
        </Button>
      </Tooltip>
    ) : (
      dash
    );
  const supervisorName = fullName(saved.assigned_supervisor);
  const hasMeta = Boolean(item.department?.name || supervisorName || item.external_id);

  /* The shared pills (`./status`), bound to this render's language. */
  const statusTag = (tones, key, options) => sharedStatusTag(tones, key, lang, options);
  const lifecycleTags = record => sharedLifecycleTags(record, lang);

  /* The sync line exists only when the record has a sync state at all. A synced record states WHEN
     as one relative phrase (absolute time on hover); a failed one shows the API's `sync_error` as it
     came — it is 1С's own message, cut to one line with the whole text on hover. */
  const syncedAbsolute = saved.synced_at ? dayjs(saved.synced_at).format("DD.MM.YYYY HH:mm") : undefined;
  const syncedAgo = relativeTime(saved.synced_at, lang);
  const syncLine = saved.sync_status ? (
    <Flex className="cp-id__sync" align="center" gap="small" wrap>
      {statusTag(SYNC_TONE, saved.sync_status, {
        fallback: "off",
        label: saved.sync_status === "synced" && syncedAgo ? t("sync_synced_at").replace("{time}", syncedAgo) : undefined,
        title: syncedAbsolute,
      })}
      {saved.sync_status === "failed" && saved.sync_error ? (
        <span className="cp-id__sync-error" title={saved.sync_error}>
          {saved.sync_error}
        </span>
      ) : null}
    </Flex>
  ) : null;

  /* One `<Form.Item>` per editable field: the label is tied to the control by id, and a refused
     field carries the error state plus the one translated phrase. */
  const formItem = (key, labelKey, control, extra) => (
    <Form.Item
      label={t(labelKey)}
      htmlFor={fieldId(key)}
      validateStatus={errors[key] ? "error" : undefined}
      help={errors[key] ? t("field_error") : undefined}
      extra={extra}>
      {control}
    </Form.Item>
  );
  const dictionarySelect = (key, labelKey) =>
    formItem(
      key,
      labelKey,
      <EntitySelect
        id={fieldId(key)}
        entity={key}
        extraQuery={DICTIONARY_QUERY[key]}
        value={item[key]?.id}
        seed={item[key]}
        placeholder={t(labelKey)}
        onChange={pickValue(key)}
      />
    );
  /* A switch with its words beside it — the label (clicking it toggles the switch, as a checkbox's
     does) and a 12px line under it: the hint, or the refusal — the lifecycle's manual switch. */
  const setting = (key, checked, onChange, label, hint, { disabled = false } = {}) => (
    <div className={disabled ? "cp-setting cp-setting--off" : "cp-setting"}>
      <Switch
        id={fieldId(key)}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-describedby={hint || errors[key] ? `${fieldId(key)}-hint` : undefined}
      />
      <div className="cp-setting__text">
        <label className="cp-setting__label" htmlFor={fieldId(key)}>
          {label}
        </label>
        {errors[key] ? (
          <div className="cp-setting__error" id={`${fieldId(key)}-hint`} role="alert">
            {t(key === "lifecycle_is_manual" ? "lc_manual_refused" : "field_error")}
          </div>
        ) : hint ? (
          <div className="cp-setting__hint" id={`${fieldId(key)}-hint`}>
            {hint}
          </div>
        ) : null}
      </div>
    </div>
  );

  /* Header and Overview split the record between them, so nothing is said twice on one screen.
     The HEAD answers "who is this and where does it stand": name, lifecycle (with the manual mark), department, the accountable supervisor, the external code, sync, points and
     balance. The OVERVIEW holds only what the head does not: how the client is segmented — the
     card's own, editable facts — and how to reach and who serves it, which come from the books.
     Labels sit ABOVE values, so a 30px status pill never sits lower than the text beside it.
     EDIT shows every editable field, the head's status ones included, since that is where they
     change. Section heads are the archetype-B `dp-sec-title` / `dp-sec-desc` styles. */
  const overviewSection = (key, titleKey, descKey, body) => (
    <section className="cp-ov" key={key} aria-labelledby={`cp-ov-${key}`}>
      <div className="dp-sec-head">
        <h3 className="dp-sec-title" id={`cp-ov-${key}`}>
          {t(titleKey)}
        </h3>
        {descKey ? <p className="dp-sec-desc">{t(descKey)}</p> : null}
      </div>
      {body}
    </section>
  );
  const facts = items => (
    <Descriptions
      className="cp-overview"
      layout="vertical"
      colon={false}
      column={{ xs: 1, sm: 2, lg: 4 }}
      items={items.map(([key, labelKey, value]) => ({ key, label: t(labelKey), children: value }))}
    />
  );
  const people = list =>
    list.length ? (
      <Flex wrap gap="small">
        {list.map(person => (
          <PersonCell key={person.id} user={person} strong={false} />
        ))}
      </Flex>
    ) : (
      dash
    );
  /* "Additional fields": the company's own, set up in the admin (`customFields.js`). No section
     at all when the company has none. VIEW sits after "Client" — card data, unlike the read-only
     "Contacts and service" from the books; EDIT is the form's last group. */
  const customFields = Array.isArray(item.custom_fields) ? item.custom_fields : [];
  const customView = customFields.length
    ? overviewSection(
        "custom",
        "ov_sec_custom",
        null,
        <Descriptions
          className="cp-overview"
          layout="vertical"
          colon={false}
          column={{ xs: 1, sm: 2, lg: 4 }}
          items={customFields.map(f => ({ key: f.id, label: f.label, children: cfDisplay(f, lang) }))}
        />
      )
    : null;
  const customEdit = customFields.length
    ? overviewSection(
        "custom",
        "ov_sec_custom",
        null,
        <div className="cp-fieldgrid">
          {customFields.map(f => {
            const error = errors[cfKey(f.id)];
            return (
              <Form.Item
                key={f.id}
                label={f.label}
                htmlFor={cfFieldId(f)}
                validateStatus={error ? "error" : undefined}
                help={error ? t(error) : undefined}
                extra={cfHint(f, lang)}>
                {cfEditor(f, value => setCustomField(f.id, value), { lang, status: error ? "error" : undefined })}
              </Form.Item>
            );
          })}
        </div>
      )
    : null;
  const overview = editing ? (
    <Form component={false} layout="vertical" colon={false}>
      <div className="dp-form cp-ov-form">
        {overviewSection(
          "client",
          "ov_sec_client",
          null,
          <div className="cp-fieldgrid">
            {dictionarySelect("client_type", "k_client_type")}
            {dictionarySelect("client_category", "k_client_category")}
            {dictionarySelect("sales_channel", "k_sales_channel")}
            {formItem(
              "start_cooperation_at",
              "k_start_cooperation",
              /* The API's value is a bare `YYYY-MM-DD` date and clearing it is `null` — never `""`,
                 which is a 400. Shown in the card's own `DD.MM.YYYY`. */
              <DatePicker
                id={fieldId("start_cooperation_at")}
                format="DD.MM.YYYY"
                placeholder={t("k_start_cooperation")}
                value={item.start_cooperation_at ? dayjs(item.start_cooperation_at) : null}
                onChange={value => setField("start_cooperation_at", value ? value.format("YYYY-MM-DD") : null)}
              />
            )}
          </div>
        )}
        {overviewSection(
          "status",
          "ov_sec_status",
          null,
          <>
            {/* Where the counterparty stands: the lifecycle with its manual / automatic mode, and the
                accountable supervisor beside it — two frames of one height. */}
            <div className="cp-stategrid">
              <div className="cp-statebox">
                {formItem(
                  "lifecycle_status",
                  "k_lifecycle",
                  <Select
                    id={fieldId("lifecycle_status")}
                    allowClear
                    value={item.lifecycle_status ?? undefined}
                    placeholder={t("k_lifecycle")}
                    options={LIFECYCLE_STATUSES.map(value => ({ value, label: t(`lc_${value}`) }))}
                    onChange={value => setStage(value ?? null)}
                  />
                )}
                {/* Manual mode needs a lifecycle value: without one the switch is off, locked, and
                    says so. */}
                {setting(
                  "lifecycle_is_manual",
                  Boolean(item.lifecycle_status) && Boolean(item.lifecycle_is_manual),
                  checked => setField("lifecycle_is_manual", checked),
                  t("lc_manual_switch"),
                  t(item.lifecycle_status ? "lc_manual_switch_hint" : "lc_manual_needs_stage"),
                  { disabled: !item.lifecycle_status }
                )}
              </div>
              {/* The accountable supervisor beside it, in a frame of its own: the same row, the same
                  height, a person shown as one (avatar + name), and a line on who can be picked. */}
              <div className="cp-statebox">
                {formItem(
                  "assigned_supervisor",
                  "k_supervisor",
                  <EntitySelect
                    id={fieldId("assigned_supervisor")}
                    entity="supervisor"
                    extraQuery={SUPERVISOR_QUERY}
                    avatar
                    value={item.assigned_supervisor?.id}
                    seed={item.assigned_supervisor}
                    placeholder={t("k_supervisor")}
                    onChange={pickSupervisor}
                    ariaDescribedBy={`${fieldId("assigned_supervisor")}-hint`}
                  />,
                  <span id={`${fieldId("assigned_supervisor")}-hint`} className="cp-setting__hint">
                    {t("k_supervisor_hint")}
                  </span>
                )}
              </div>
            </div>
          </>
        )}
        {customEdit}
      </div>
    </Form>
  ) : (
    <>
      {/* A stage set by hand, said in words with the way back — VIEW only (while editing, the form's
          switch does it). Calm, not a warning: nothing is wrong, it is a mode. */}
      {saved.lifecycle_is_manual && saved.lifecycle_status ? (
        <div className="cp-manualnote" role="note">
          <UserSwitchOutlined className="cp-manualnote__icon" aria-hidden="true" />
          <div className="cp-manualnote__body">
            <span>{t("lc_manual_banner").replace("{stage}", t(`lc_${saved.lifecycle_status}`))}</span>{" "}
            <Button type="link" size="small" className="cp-manualnote__act" onClick={revertManual}>
              {t("lc_manual_revert")}
            </Button>
          </div>
        </div>
      ) : null}
      {overviewSection(
        "client",
        "ov_sec_client",
        null,
        facts([
          ["client_type", "k_client_type", textOf(item.client_type?.label)],
          ["client_category", "k_client_category", textOf(item.client_category?.label)],
          ["sales_channel", "k_sales_channel", textOf(item.sales_channel?.label)],
          ["start_cooperation_at", "k_start_cooperation", item.start_cooperation_at ? dayjs(item.start_cooperation_at).format("DD.MM.YYYY") : dash],
        ])
      )}
      {customView}
      {/* From the books, so read-only here — the API refuses a write to any of it. The note names
          no system on purpose (which books feed this deployment is the deployment's business). */}
      {overviewSection(
        "contacts",
        "ov_sec_contacts",
        "ov_sec_account_desc",
        facts([
          ["address", "k_address", textOf(item.address)],
          ["phone_number", "k_phone", textOf(item.phone_number)],
          ["merchandisers", "k_responsible", people(Array.isArray(item.merchandisers) ? item.merchandisers.filter(fullName) : [])],
        ])
      )}
    </>
  );

  const activeSection = SECTIONS.find(s => s.key === tab);
  const sectionBody =
    activeSection.key === "contacts" ? (
      <ContactsTab counterpartyId={item.id} lang={lang} />
    ) : activeSection.key === "addresses" ? (
      <AddressesTab
        item={item}
        lang={lang}
        leaveTo={leaveTo}
        onPoints={mergePoints}
        onPointAdded={mergePoint}
        onCreatePoint={openCreatePoint}
        createIntent={createIntent}
        onIntentDone={createIntentDone}
      />
    ) : activeSection.key === "orders" ? (
      <OrdersTab
        counterpartyId={item.id}
        lang={lang}
        cardLocation={{ pathname: location.pathname, search: "?tab=orders", state: location.state }}
        leaveTo={leaveTo}
      />
    ) : activeSection.key === "returns" ? (
      <ReturnsTab
        counterpartyId={item.id}
        lang={lang}
        cardLocation={{ pathname: location.pathname, search: "?tab=returns", state: location.state }}
        leaveTo={leaveTo}
      />
    ) : activeSection.key === "payments" ? (
      <PaymentsTab counterpartyId={item.id} lang={lang} leaveTo={leaveTo} debtTo={debtTo} />
    ) : activeSection.key === "comments" ? (
      <CommentsTab counterpartyId={item.id} lang={lang} draft={commentDraft} setDraft={setCommentDraft} />
    ) : activeSection.key === "debts" ? (
      <DebtsTab counterpartyId={item.id} lang={lang} highlightId={debtParam} />
    ) : (
      <>
        {overview}
        <LinksSection
          counterpartyId={item.id}
          links={Array.isArray(item.linked_counterparties) ? item.linked_counterparties : []}
          lang={lang}
          onChange={mergeLinks}
          linkTo={linkTo}
          onOpen={openLinked}
        />
      </>
    );

  return (
    <div className="shell-content cp-detail">
      {/* Command bar — breadcrumb + actions. Non-sticky on purpose: the card is short and has a
          couple of actions, so it skips the visit page's measured sticky-offset machinery (the same
          call `/supervisors/:id` and `/answers` made). */}
      <Flex className="cp-bar" align="center" gap="middle" wrap>
        <Flex className="cp-bar__crumb" align="center" gap={6}>
          <Typography.Link href={history.createHref({ pathname: "/sales/counterparties", search: backSearch })} onClick={linkClick(leaveTo, backTo)}>
            {translate(SIDE_NAV, "counterparties", lang)}
          </Typography.Link>
          <RightOutlined className="cp-crumb__sep" />
          <Typography.Text strong ellipsis>
            {name}
          </Typography.Text>
        </Flex>
        <div className="cp-spacer" />
        <Space size={8} wrap>
          {editing ? (
            <>
              <Button type="primary" icon={<SaveOutlined />} loading={busy} disabled={!dirty} onClick={save}>
                {t("edit_save")}
              </Button>
              <Button icon={<CloseOutlined />} onClick={cancelEdit}>
                {t("edit_cancel")}
              </Button>
            </>
          ) : (
            <>
              {/* Only on the Overview: it is the one section the edit mode changes; the document tabs
                  are read-only. An edit already started stays (and keeps Save / Cancel) on any tab. */}
              {tab === "overview" ? (
                <Button icon={<EditOutlined />} onClick={() => setMode("EDIT")}>
                  {t("edit_btn")}
                </Button>
              ) : null}
              <Button icon={<LinkOutlined />} onClick={copyLink}>
                {t("card_copy_link")}
              </Button>
            </>
          )}
        </Space>
      </Flex>

      {/* Identity card — the mockup's header shape: an info row (square shot + icon-led title/meta)
          over a divided metrics strip. The same building blocks as the visit card (`vd-id`), only
          the metrics move under a rule instead of sitting in a right-hand block. */}
      <Card className="cp-id" variant="outlined">
        <div className={editing ? "cp-id__top cp-id__top--edit" : "cp-id__top"}>
          <Flex vertical align="center" gap={8} className="cp-id__shot">
            <Avatar shape="square" size={52} className="cp-logo" style={{ fontSize: 20 }} src={photoPreview || item.photo_url || null} onError={() => true}>
              {initialsOf(name)}
            </Avatar>
            {editing ? (
              <Upload
                beforeUpload={file => {
                  setPhotoFile(file);
                  setPhotoPreview(URL.createObjectURL(file));
                  setPhotoError(false);
                  return false;
                }}
                maxCount={1}
                showUploadList={false}
                accept="image/*">
                <Button size="small" icon={<UploadOutlined />}>
                  {t("edit_replace_photo")}
                </Button>
              </Upload>
            ) : null}
            {editing && photoError ? (
              <Typography.Text className="cp-photo-error" role="alert">
                {t("photo_error")}
              </Typography.Text>
            ) : null}
          </Flex>

          <div className="cp-id__main">
            <Flex className="cp-id__head" align="center" gap={12} wrap>
              <Typography.Text strong className="cp-id__t">
                {name}
              </Typography.Text>
              {lifecycleTags(saved)}
            </Flex>
            {hasMeta ? (
              <Flex className="cp-id__sub" align="center" gap="small" wrap>
                {item.department?.name ? (
                  <span>
                    <ApartmentOutlined /> {item.department.name}
                  </span>
                ) : null}
                {/* The ACCOUNTABLE person only. The merchandisers who serve the client are listed,
                    with faces, in the Overview — once, not here as well. `TeamOutlined` is the
                    supervisors glyph in spec/02's icon map; the span names the relation on hover. */}
                {supervisorName ? (
                  <span title={t("k_supervisor")}>
                    <TeamOutlined /> {supervisorName}
                  </span>
                ) : null}
                {item.external_id ? (
                  <span>
                    <NumberOutlined /> {item.external_id}
                  </span>
                ) : null}
              </Flex>
            ) : null}
            {syncLine}
          </div>
        </div>

        {/* Metrics — the SHARED `KpiStrip` the order / return detail headers use (`.dp-kpi*`): equal
            centred tiles, a divider between them and its own top rule; 2 × 2 on a phone
            (`counterparties.scss`), the shared stack left to the order / return pages. */}
        <KpiStrip
          items={[
            { key: "orders", label: t("m_orders_90d"), value: ordersKpi },
            { key: "remaining", label: t("m_remaining"), value: money(item.remaining_funds) },
            /* `points_count`: every point not deleted, inactive ones included — not the length of
               `points`, which is the same set today but is the block's list, not the count. */
            { key: "points", label: t("m_points"), value: item.points_count ?? 0 },
            /* The SAVED date — an unsaved edit in the Overview form is not yet the counterparty's. */
            {
              key: "since",
              label: t("m_cooperation_since"),
              value: saved.start_cooperation_at ? <span className="lp-num">{dayjs(saved.start_cooperation_at).format("DD.MM.YYYY")}</span> : dash,
            },
          ]}
        />
        {/* AD-4, on every tab: without a point the counterparty cannot be worked in the field. */}
        {item.points_count === 0 ? <NoPointsNotice lang={lang} className="cp-nopoints" onGo={openCreatePoint} /> : null}
      </Card>

      {/* Section rail + section card — the visit page's `vd-rail` / `vd-sec` shape (its own `cp-*`
          classes, since `visit-detail.scss` is not globally imported). One section mounted at a
          time; the rail is non-sticky here (short page, no measured-offset machinery). */}
      <div className="cp-rail" role="tablist" aria-label={translate(VISIT_PAGE, "sections", lang)}>
        {SECTIONS.map(section => (
          <button key={section.key} type="button" role="tab" aria-selected={section.key === tab} onClick={() => selectTab(section.key)}>
            {section.icon}
            {t(section.labelKey)}
          </button>
        ))}
      </div>

      <Card className="cp-sec" variant="outlined" role="tabpanel">
        {/* One heading per section; the Orders and Returns tabs hang "Read-only", their window and
            the way to the full list on it rather than repeating their own title above the table. */}
        <Flex className="cp-sec__h" align="center" justify="space-between" gap="small" wrap>
          <Flex align="center" gap="small">
            {activeSection.icon}
            {t(activeSection.labelKey)}
            {DOC_TABS[activeSection.key] ? <ReadOnlyTag lang={lang} /> : null}
          </Flex>
          {DOC_TABS[activeSection.key] ? (
            <TabHead counterpartyId={item.id} counterpartyName={name} lang={lang} leaveTo={leaveTo} {...DOC_TABS[activeSection.key]} />
          ) : null}
        </Flex>
        {sectionBody}
      </Card>
    </div>
  );
};

const Page = () => {
  const { id } = useParams();
  return <CardPage key={id} id={id} />;
};

export default Page;
